import {exists} from '@aboutyou/utils';
import type {GrpcResponseError} from './GrpcClientChannel';

/**
 * A list of possible GRPC status codes.
 * @todo Could also be exported using `@improbable-eng/grpc-web` but this increased the bundle size as the library is not code splittable/ does not export a esmodule build
 * @see https://github.com/improbable-eng/grpc-web/blob/a217aa7cee7df3cbbbdd0eb4d0d01836fd839306/client/grpc-web/docs/concepts.md#grpccode
 */
enum GrpcCode {
  InvalidArgument = 3,
  NotFound = 5,
  AlreadyExists = 6,
  FailedPrecondition = 9,
  Unimplemented = 12,
  Unavailable = 14,
  Unauthenticated = 16,
  Unknown = 2,
  Internal = 13,
}

enum CustomCode {
  CLIENT_TIMEOUT_EXCEEDED = 408,
}

export const isNotFoundError = (error: unknown) => {
  return (
    exists(error) && (error as GrpcResponseError)?.code === GrpcCode.NotFound
  );
};

export const isInvalidArgumentError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.InvalidArgument
  );
};

export const isUnimplementedError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.Unimplemented
  );
};

export const isUnavailableError = (error: unknown) => {
  return (
    exists(error) && (error as GrpcResponseError)?.code === GrpcCode.Unavailable
  );
};

export const isAlreadyExistsError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.AlreadyExists
  );
};

export const isFailedPreconditionError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.FailedPrecondition
  );
};

export const isUnauthenticatedError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.Unauthenticated
  );
};

export const isClientTimeoutExceededError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === CustomCode.CLIENT_TIMEOUT_EXCEEDED
  );
};

// A gRPC-web response stream that is severed mid-flight surfaces as
// `grpc-status 13 (Internal) / "unexpected EOF"`. In production this is
// dominated by datacenter/bot traffic and transient edge/gateway stream drops
// rather than a frontend fault, so it should not be recorded as a critical
// error. See ASFE-14177 for the recurring monitor noise this addresses.
export const isUnexpectedEofError = (error: unknown) => {
  return (
    exists(error) &&
    (error as GrpcResponseError)?.code === GrpcCode.Internal &&
    /unexpected EOF/i.test((error as GrpcResponseError)?.message ?? '')
  );
};

class GrpcNetworkTimeoutError extends Error {
  constructor(message: string) {
    super(`${message}`);
    Object.setPrototypeOf(this, GrpcNetworkTimeoutError.prototype);
  }
}

export const createClientTimeoutExceededError = () => {
  const error: GrpcResponseError = new GrpcNetworkTimeoutError(
    'Network request cancelled by timeout before gRPC is resolved',
  );
  error.code = CustomCode.CLIENT_TIMEOUT_EXCEEDED;
  return error;
};

export const getRawGrpcResponseErrorStatus = (
  error: GrpcResponseError,
): number => {
  const fallbackErrorCode = error.code ?? GrpcCode.Unknown;

  if (exists(error) && error?.metadata?.get('http-status-code')?.[0]) {
    const rawStatusCode = Number(error.metadata.get('http-status-code')[0]);

    return isNaN(rawStatusCode) ? fallbackErrorCode : rawStatusCode;
  }
  return fallbackErrorCode;
};
